import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  styled,
  useMediaQuery,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Pagination,
  Grid,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CallIcon from "@mui/icons-material/Call";
import PercentIcon from "@mui/icons-material/Percent";
import { AppDispatch, RootState } from "../../stores";
import { getApiUsageReports } from "../../features/generate-report-module/reportAction";
import { resetReportState } from "../../features/generate-report-module/reportSlice";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100%",
  padding: theme.spacing(3),
  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  width: "100%",
  tableLayout: "auto",
  "& .MuiTableRow-root": {
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
  },
  [theme.breakpoints.down("md")]: {
    minWidth: 600,
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 500,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
  padding: theme.spacing(1, 0.5),
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "rgba(75, 91, 93, 0.04)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: theme.spacing(1),
  },
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#f5f0e7",
  fontWeight: 600,
  padding: theme.spacing(1.2, 0.5),
  lineHeight: "1.2rem",
  cursor: "pointer",
  userSelect: "none",
  "&:hover": {
    backgroundColor: "#ede7d9",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const OverallStatsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "linear-gradient(135deg, #f5f0e7 0%, #e8e3d8 100%)",
  borderRadius: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  marginBottom: theme.spacing(3),
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "scale(1.02)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5),
  },
}));

const FailureProgress = styled(CircularProgress)(({ theme, value }) => ({
  color:
    value! > 50
      ? theme.palette.error.main
      : value! > 20
      ? theme.palette.warning.main
      : theme.palette.success.main,
  "& .MuiCircularProgress-circle": {
    strokeLinecap: "round",
  },
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  marginTop: "auto",
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

// Loader Container
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  minHeight: "300px",
  width: "100%",
  height: "100%",
  flexGrow: 1,
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

interface TableApiUsage {
  no: number;
  fullName: string;
  totalCalls: number;
  failures: number;
  failurePercentage: number;
  userId: string;
}

const APIUsageTable: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const {
    apiUsageOverall,
    apiUsagePerCustomer,
    apiUsageLoading,
    apiUsageError,
    apiUsageTotal,
    apiUsageTotalPages,
  } = useSelector((state: RootState) => state.report);
  const [tableApiUsage, setTableApiUsage] = useState<TableApiUsage[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch API usage reports when pagination changes or on mount
  useEffect(() => {
    dispatch(getApiUsageReports({ page: currentPage, limit: pageSize }));
    return () => {
      dispatch(resetReportState());
    };
  }, [dispatch, currentPage, pageSize]);

  // Map API usage data to table format
  useEffect(() => {
    const mappedApiUsage: TableApiUsage[] = apiUsagePerCustomer.map(
      (usage, index) => ({
        no: (currentPage - 1) * pageSize + index + 1,
        fullName: usage.fullName,
        totalCalls: usage.totalCalls,
        failures: usage.failures,
        failurePercentage: Number(usage.failurePercentage.toFixed(2)),
        userId: usage.userId,
      })
    );
    setTableApiUsage(mappedApiUsage);
  }, [apiUsagePerCustomer, currentPage, pageSize]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} color="#4b5b5d">
          API Usage Table
        </Typography>

        {/* Page Size Selector */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: isMobile ? "center" : "flex-end",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <Typography variant="body2" color="#4b5b5d">
            Show:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              sx={{
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                  fontSize: "0.8rem",
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="#4b5b5d">
            entries
          </Typography>
        </Box>
      </Box>

      {/* Overall API Usage Stats */}
      {apiUsageOverall && (
        <OverallStatsContainer>
          <Typography variant="h6" fontWeight={600} color="#4b5b5d" mb={2}>
            Overall API Usage
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard>
                <CallIcon sx={{ color: "#4b5b5d", fontSize: "2rem" }} />
                <Box>
                  <Typography variant="body2" color="#4b5b5d" fontWeight={500}>
                    Total Calls
                  </Typography>
                  <Typography variant="h5" color="#4b5b5d" fontWeight={600}>
                    {apiUsageOverall.totalCalls}
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard>
                <ErrorOutlineIcon sx={{ color: "#4b5b5d", fontSize: "2rem" }} />
                <Box>
                  <Typography variant="body2" color="#4b5b5d" fontWeight={500}>
                    Failures
                  </Typography>
                  <Typography variant="h5" color="#4b5b5d" fontWeight={600}>
                    {apiUsageOverall.failures}
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <FailureProgress
                    variant="determinate"
                    value={Math.min(apiUsageOverall.failurePercentage, 100)}
                    size={48}
                    thickness={4}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PercentIcon sx={{ color: "#4b5b5d", fontSize: "1rem" }} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="#4b5b5d" fontWeight={500}>
                    Failure Percentage
                  </Typography>
                  <Typography variant="h5" color="#4b5b5d" fontWeight={600}>
                    {apiUsageOverall.failurePercentage.toFixed(2)}%
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
          </Grid>
        </OverallStatsContainer>
      )}

      {apiUsageLoading && (
        <LoaderContainer>
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: "#4b5b5d",
            }}
          />
          <LoaderText>Loading API usage data...</LoaderText>
        </LoaderContainer>
      )}

      {!apiUsageLoading && apiUsageError && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 2,
            flexGrow: 1,
          }}
        >
          <Typography color="error" sx={{ textAlign: "center" }}>
            {apiUsageError}
          </Typography>
        </Box>
      )}

      {!apiUsageLoading && !apiUsageError && (
        <Box
          sx={{
            overflowX: "auto",
            "-webkit-overflow-scrolling": "touch",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#4b5b5d",
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#354142",
            },
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: tableApiUsage.length === 0 ? "center" : "flex-start",
          }}
        >
          {tableApiUsage.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                color="#4b5b5d"
                sx={{
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                No API Usage Data Available
              </Typography>
            </Box>
          ) : (
            <StyledTable size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCell>No.</StyledTableHeadCell>
                  <StyledTableHeadCell>Full Name</StyledTableHeadCell>
                  <StyledTableHeadCell>Total Calls</StyledTableHeadCell>
                  <StyledTableHeadCell>Failures</StyledTableHeadCell>
                  <StyledTableHeadCell>Failure %</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableApiUsage.map((usage) => (
                  <TableRow key={usage.userId}>
                    <StyledTableCell>{usage.no}.</StyledTableCell>
                    <StyledTableCell>{usage.fullName}</StyledTableCell>
                    <StyledTableCell>{usage.totalCalls}</StyledTableCell>
                    <StyledTableCell>{usage.failures}</StyledTableCell>
                    <StyledTableCell>
                      {usage.failurePercentage}%
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          )}
        </Box>
      )}

      {/* Pagination Section */}
      {!apiUsageLoading && tableApiUsage.length > 0 && (
        <PaginationContainer>
          <Typography
            variant="body2"
            color="#4b5b5d"
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            Showing {(currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, apiUsageTotal)} of {apiUsageTotal}{" "}
            entries
          </Typography>

          <Pagination
            count={apiUsageTotalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size={isMobile ? "small" : "medium"}
            showFirstButton
            showLastButton
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#4b5b5d",
                "&.Mui-selected": {
                  backgroundColor: "#f5f0e7",
                  color: "#4b5b5d",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#ede7d9",
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(75, 91, 93, 0.04)",
                },
              },
            }}
          />
        </PaginationContainer>
      )}
    </Container>
  );
};

export default APIUsageTable;
